import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import type {
  MetaAdDraftInput,
  CampaignObjective,
  BudgetMode,
  BudgetConfig,
} from '../../../types/metaAdDraft';
import type { EligibleAccount, PageSummary } from '../../../types/metaAdResources';
import {
  CUSTOM_EVENT_TYPE_LABELS,
  LEADS_CLASS_EVENTS,
  OPTIMIZATION_GOAL_LABELS,
  SALES_CLASS_EVENTS,
  validOptimizationGoals,
} from '../../../lib/metaAdObjectiveShape';
import { facebookPageUrl, instagramUrl, metaAdAccountUrl } from '../../../lib/connectionLinks';
import { getEligibleAccounts, getPages } from '../api';
import { applyDestinationType } from '../destination';
import { igDefaultPatch } from '../igDefault';
import { accountDefaultsPatch } from '../accountDefaults';
import { useLinkedAdAccount } from '../useLinkedAdAccount';
import { RadioGroup } from '../components/RadioGroup';
import { BudgetFields } from '../components/BudgetFields';
import { ScheduleDateTimeField } from '../components/ScheduleDateTimeField';
import { PlacementsSection } from './PlacementsSection';
import { DEFAULT_END_TIME, DEFAULT_START_TIME } from '../../../lib/scheduleTime';

// Multi-ad-set: the Campaign section owns every CAMPAIGN-WIDE-LOCKED field —
// objective, budget mode (CBO/ABO) + the CBO budget, optimization goal + event,
// destination, the connected Meta account (Page / Ad Account / Instagram),
// placements, and the default schedule. Per-ad-set targeting / ABO budget / date
// overrides live on each ad set's card (AdSetSection).

const OBJECTIVE_OPTIONS = [
  { value: 'TRAFFIC', label: 'Traffic' },
  { value: 'LEADS', label: 'Leads' },
];
const BUDGET_MODE_OPTIONS = [
  { value: 'ABO' as BudgetMode, label: 'Ad set budget (ABO)', description: 'Set the budget on each ad set' },
  { value: 'CBO' as BudgetMode, label: 'Campaign budget (CBO)', description: "Meta's recommended — distributes spend across ad sets" },
];


// Render `children` as an external deep-link when `href` is set, else plain text.
function MetaLink({ href, children }: { href: string | null; children: ReactNode }) {
  if (!href) return <>{children}</>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-kyck-600 hover:underline">
      {children}
    </a>
  );
}

export function CampaignSection({
  value,
  onChange,
}: {
  value: MetaAdDraftInput;
  onChange: (patch: Partial<MetaAdDraftInput>) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pageOpen, setPageOpen] = useState(false);
  const eligible = useQuery({ queryKey: ['metaAdEligibleAccounts'], queryFn: getEligibleAccounts });
  const { currency, timezoneName, adAccountId, isLoading: adAccountLoading } = useLinkedAdAccount(value.accountId);

  const selected = eligible.data?.accounts.find((a) => a.id === value.accountId) ?? null;

  const pages = useQuery({
    queryKey: ['metaAdPages', value.accountId],
    queryFn: () => getPages(value.accountId),
    enabled: value.accountId.trim().length > 0,
  });
  const selectedPage = pages.data?.pages.find((p) => p.id === value.campaign.facebookPageId) ?? null;
  const connectedPages = (pages.data?.pages ?? []).filter((p) => p.isSelected);
  const connectedPage = connectedPages.length === 1 ? connectedPages[0]! : null;
  const multiConnected = connectedPages.length > 1;

  // Instagram delivery is driven by the ACCOUNT's IG (surfaced through the shared
  // ad's ads.instagramAccountId, seeded on account-pick), not the Page's native link.
  const acct = eligible.data?.accounts.find((a) => a.id === value.accountId) ?? null;
  const igId = (value.ads.instagramAccountId ?? '').trim() || acct?.defaultInstagramAccountId || '';
  const igName = (value.ads.instagramAccountName ?? '').trim() || acct?.defaultInstagramAccountName || '';
  const hasAccountIg = igId !== '';

  const pageHref = connectedPage ? facebookPageUrl(connectedPage.id) : null;
  const adAccountHref = metaAdAccountUrl(adAccountId);
  const igHref = instagramUrl(igName);
  const pageIgHref = instagramUrl(connectedPage?.instagramHandle ?? null);

  // Auto-set the campaign Page from the client's single connected Page.
  useEffect(() => {
    if (!connectedPage) return;
    if (
      value.campaign.facebookPageId === connectedPage.id &&
      value.campaign.facebookPageName === connectedPage.name
    ) {
      return;
    }
    onChange({
      campaign: { ...value.campaign, facebookPageId: connectedPage.id, facebookPageName: connectedPage.name },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync only on the resolved page id/name
  }, [connectedPage?.id, connectedPage?.name]);

  const objective = value.campaign.objective;
  const isTraffic = objective === 'TRAFFIC';
  const destinationOptions = isTraffic
    ? [{ value: 'website' as const, label: 'Website' }]
    : [
        { value: 'instant_form' as const, label: 'Instant form', description: 'Collect leads in-app' },
        { value: 'website' as const, label: 'Website' },
      ];

  const optGoals = validOptimizationGoals(objective, value.campaign.destinationType);
  const isConversions = value.campaign.optimizationGoal === 'OFFSITE_CONVERSIONS';

  function pickAccount(acc: EligibleAccount) {
    const patch: Partial<MetaAdDraftInput> = { accountId: acc.id };
    // Seed the shared ad's account-level defaults — IG delivery identity (D2.11)
    // + pixel / lead form (Feature A) — filling ONLY empty selectors so a
    // reopened/edited draft's existing choices are never clobbered.
    let nextAds = value.ads;
    const ig = igDefaultPatch(nextAds.instagramAccountId, acc);
    if (ig) nextAds = { ...nextAds, ...ig };
    const defaults = accountDefaultsPatch(nextAds, acc);
    if (defaults) nextAds = { ...nextAds, ...defaults };
    if (nextAds !== value.ads) patch.ads = nextAds;
    onChange(patch);
    setPickerOpen(false);
  }

  function setObjective(objective: CampaignObjective) {
    const withObjective = { ...value, campaign: { ...value.campaign, objective } };
    const destinationType = objective === 'TRAFFIC' ? 'website' : value.campaign.destinationType;
    onChange(applyDestinationType(withObjective, destinationType));
  }

  function setOptimizationGoal(optimizationGoal: string) {
    const customEventType =
      optimizationGoal === 'OFFSITE_CONVERSIONS' ? value.campaign.customEventType ?? 'LEAD' : null;
    onChange({ campaign: { ...value.campaign, optimizationGoal, customEventType } });
  }

  function setBudgetMode(mode: BudgetMode) {
    if (mode === 'CBO') {
      // CBO: budget on the campaign; clear every ad set's budget.
      onChange({
        campaign: {
          ...value.campaign,
          budgetMode: 'CBO',
          budget: value.campaign.budget ?? { amountCents: 0, type: 'daily' },
        },
        adSets: value.adSets.map((a) => ({ ...a, budget: null })),
      });
    } else {
      // ABO: budget on each ad set (managed in the card); clear the campaign budget.
      onChange({ campaign: { ...value.campaign, budgetMode: 'ABO', budget: null } });
    }
  }

  function setCampaignBudget(budget: BudgetConfig) {
    onChange({ campaign: { ...value.campaign, budget } });
  }

  function pickPage(p: PageSummary) {
    onChange({ campaign: { ...value.campaign, facebookPageId: p.id, facebookPageName: p.name } });
    setPageOpen(false);
  }

  return (
    <section id="campaign" className="space-y-6">
      <h2 className="text-lg font-semibold text-slate-900">Campaign</h2>

      {/* Account picker */}
      <div className="max-w-xl">
        <label className="mb-1.5 block text-sm font-medium text-slate-700">Client account</label>
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-haspopup="listbox"
              className="flex w-full items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2 text-left text-sm hover:border-slate-400"
            >
              <span className={selected ? 'text-slate-900' : 'text-slate-400'}>
                {selected ? selected.name : 'Select a connected account…'}
              </span>
              <ChevronsUpDown className="h-4 w-4 text-slate-400" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search accounts…" />
              <CommandList>
                <CommandEmpty>No connected accounts.</CommandEmpty>
                <CommandGroup>
                  {(eligible.data?.accounts ?? []).map((acc) => (
                    <CommandItem key={acc.id} value={acc.name} onSelect={() => pickAccount(acc)}>
                      <Check
                        className={['mr-2 h-4 w-4', acc.id === value.accountId ? 'opacity-100' : 'opacity-0'].join(' ')}
                      />
                      <span className="flex-1">{acc.name}</span>
                      <span className="ml-2 text-xs text-slate-400">{acc.profileName}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
          {currency && (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-600">{currency}</span>
          )}
          {timezoneName && <span className="text-slate-500">{timezoneName}</span>}
          {adAccountLoading && <span className="text-slate-400">loading ad account…</span>}
        </div>
        {(eligible.data?.unlinkedCount ?? 0) > 0 && (
          <p className="mt-1 text-xs text-slate-400">
            {eligible.data!.unlinkedCount} more account{eligible.data!.unlinkedCount === 1 ? '' : 's'} need a Meta
            connection. Manage in Settings.
          </p>
        )}
      </div>

      {/* Campaign objective (D2.17) */}
      <div className="max-w-xs">
        <label htmlFor="campaign-objective" className="mb-1.5 block text-sm font-medium text-slate-700">
          Campaign Objective
        </label>
        <select
          id="campaign-objective"
          value={value.campaign.objective}
          onChange={(e) => setObjective(e.target.value as CampaignObjective)}
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
        >
          {OBJECTIVE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-slate-400">
          Traffic = drive clicks to a website. Leads = capture lead form submissions or website conversions.
        </p>
      </div>

      {/* Destination — campaign-wide; Traffic locks to Website (validator-consistent). */}
      <RadioGroup
        label="Destination"
        value={value.campaign.destinationType}
        options={destinationOptions}
        onChange={(d) => onChange(applyDestinationType(value, d))}
      />
      {isTraffic && (
        <p className="-mt-3 text-xs text-slate-400">Traffic campaigns send people to a website.</p>
      )}

      {/* Optimization goal + custom event type (D2.17) */}
      <div className="max-w-xs">
        <label htmlFor="campaign-opt-goal" className="mb-1.5 block text-sm font-medium text-slate-700">
          Optimization Goal
        </label>
        <select
          id="campaign-opt-goal"
          value={value.campaign.optimizationGoal}
          onChange={(e) => setOptimizationGoal(e.target.value)}
          disabled={optGoals.length === 1}
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500 disabled:bg-slate-50 disabled:text-slate-500"
        >
          {optGoals.map((g) => (
            <option key={g} value={g}>
              {OPTIMIZATION_GOAL_LABELS[g] ?? g}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-slate-400">
          What Meta optimizes delivery for (shared across all ad sets). “Conversions” requires pixel + event setup.
        </p>
      </div>

      {isConversions && (
        <div className="max-w-xs">
          <label htmlFor="campaign-event-type" className="mb-1.5 block text-sm font-medium text-slate-700">
            Optimization Event
          </label>
          <select
            id="campaign-event-type"
            value={value.campaign.customEventType ?? 'LEAD'}
            onChange={(e) => onChange({ campaign: { ...value.campaign, customEventType: e.target.value } })}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
          >
            <optgroup label="Lead Events">
              {LEADS_CLASS_EVENTS.map((ev) => (
                <option key={ev} value={ev}>
                  {CUSTOM_EVENT_TYPE_LABELS[ev] ?? ev}
                </option>
              ))}
            </optgroup>
            <optgroup label="Sales Events">
              {SALES_CLASS_EVENTS.map((ev) => (
                <option key={ev} value={ev}>
                  {CUSTOM_EVENT_TYPE_LABELS[ev] ?? ev}
                </option>
              ))}
            </optgroup>
          </select>
          <p className="mt-1 text-xs text-slate-400">
            Lead events optimize for Meta’s Leads objective; Sales events optimize for the Sales objective.
          </p>
        </div>
      )}

      {/* Meta Account Details — the SINGLE read-only summary of the client's Meta
          connection (Facebook Page · Ad Account · Instagram). To change it, switch
          the client above. */}
      <div className="max-w-xl">
        <label className="mb-1.5 block text-sm font-medium text-slate-700">Meta Account Details</label>
        <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm">
          {/* Facebook Page */}
          <div className="flex gap-2">
            <span className="w-28 shrink-0 text-slate-500">Facebook Page</span>
            <span className="flex-1">
              {!value.accountId ? (
                <span className="text-slate-400">Pick a client first.</span>
              ) : pages.isLoading ? (
                <span className="text-slate-400">Loading…</span>
              ) : pages.isError ? (
                <span className="text-amber-700">Couldn’t load the Page.</span>
              ) : multiConnected ? (
                <Popover open={pageOpen} onOpenChange={setPageOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between rounded-md border border-slate-300 bg-white px-2 py-1 text-left text-sm hover:border-slate-400"
                    >
                      <span className={selectedPage ? 'text-slate-900' : 'text-slate-400'}>
                        {selectedPage ? selectedPage.name : 'Select a Page…'}
                      </span>
                      <ChevronsUpDown className="h-4 w-4 text-slate-400" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search Pages…" />
                      <CommandList>
                        <CommandEmpty>No Pages connected for this client.</CommandEmpty>
                        <CommandGroup>
                          {connectedPages.map((p) => (
                            <CommandItem key={p.id} value={p.name} onSelect={() => pickPage(p)}>
                              <Check
                                className={['mr-2 h-4 w-4', p.id === value.campaign.facebookPageId ? 'opacity-100' : 'opacity-0'].join(' ')}
                              />
                              <span className="flex-1">{p.name}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              ) : connectedPage ? (
                <span className="text-slate-900">
                  <MetaLink href={pageHref}>{connectedPage.name}</MetaLink>
                  {connectedPage.username && <span className="text-slate-500"> · @{connectedPage.username}</span>}
                </span>
              ) : (
                <span className="text-amber-700">
                  No Facebook Page connected for this client — connect it on the account page.
                </span>
              )}
            </span>
          </div>

          {/* Ad Account */}
          <div className="flex gap-2">
            <span className="w-28 shrink-0 text-slate-500">Ad Account</span>
            <span className="flex-1 text-slate-900">
              {adAccountId ? (
                <MetaLink href={adAccountHref}>{adAccountId}</MetaLink>
              ) : value.accountId ? (
                <span className="text-slate-400">…</span>
              ) : (
                '—'
              )}
            </span>
          </div>

          {/* Instagram */}
          <div className="flex gap-2">
            <span className="w-28 shrink-0 text-slate-500">Instagram</span>
            <span className="flex-1">
              {hasAccountIg ? (
                <span className="text-slate-900">
                  {igId}
                  {igName && (
                    <span className="text-slate-500">
                      {' · '}
                      <MetaLink href={igHref}>{igName}</MetaLink>
                    </span>
                  )}
                </span>
              ) : connectedPage?.hasInstagramLinked ? (
                <span className="text-slate-700">
                  Auto-derived from the Page
                  {connectedPage.instagramHandle ? (
                    <>
                      {' ('}
                      <MetaLink href={pageIgHref}>{connectedPage.instagramHandle}</MetaLink>
                      {')'}
                    </>
                  ) : (
                    ''
                  )}
                </span>
              ) : (
                <span className="text-slate-600">No Instagram configured — runs Facebook placements only.</span>
              )}
            </span>
          </div>
        </div>
        <p className="mt-1 text-xs text-slate-400">
          Connected via the selected client. To change, switch the client above.
        </p>
      </div>

      {isConversions && (
        <div className="max-w-xl rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800">
          Conversions optimization tells Meta to optimize delivery for people likely to take your selected
          pixel event. Make sure your pixel is firing the{' '}
          <span className="font-medium">{value.campaign.customEventType ?? 'LEAD'}</span> event correctly before
          publishing — and select that pixel in the Ad section.
        </div>
      )}

      {/* Budget mode */}
      <RadioGroup
        label="Budget level"
        value={value.campaign.budgetMode}
        options={BUDGET_MODE_OPTIONS}
        onChange={setBudgetMode}
      />

      {/* CBO budget (only when budgetMode === 'CBO') */}
      {value.campaign.budgetMode === 'CBO' && (
        <BudgetFields
          label="Campaign budget"
          value={value.campaign.budget}
          currency={currency}
          timezoneName={timezoneName}
          onChange={setCampaignBudget}
        />
      )}

      {/* Placements (campaign-wide-locked) */}
      <PlacementsSection value={value} onChange={onChange} />

      {/* Default schedule — ad sets inherit these unless they override. Date+time
          (default 5am start / 10pm end), resolved against the effective timezone. */}
      <div className="space-y-4">
        <div className="flex max-w-xl flex-wrap gap-6">
          <div className="flex-1">
            <ScheduleDateTimeField
              label="Default start"
              value={value.campaign.defaultStartDate}
              defaultTime={DEFAULT_START_TIME}
              onChange={(next) => onChange({ campaign: { ...value.campaign, defaultStartDate: next } })}
              hint="Leave blank to go live immediately. Default 5:00am."
            />
          </div>
          <div className="flex-1">
            <ScheduleDateTimeField
              label="Default end"
              value={value.campaign.defaultEndDate}
              defaultTime={DEFAULT_END_TIME}
              onChange={(next) => onChange({ campaign: { ...value.campaign, defaultEndDate: next } })}
              hint="Leave blank for an open-ended run. Default 10:00pm."
            />
          </div>
        </div>

        {/* Timezone resolution (B) — account tz by default, AU-eastern override. */}
        <div className="max-w-xl space-y-1.5">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <input
              type="checkbox"
              checked={value.campaign.scheduleUseAustralianEastern}
              onChange={(e) => onChange({ campaign: { ...value.campaign, scheduleUseAustralianEastern: e.target.checked } })}
              className="h-4 w-4 rounded border-slate-300 text-kyck-600 focus:ring-kyck-500"
            />
            Override to Australian Eastern time
          </label>
          <p className="text-xs text-slate-400">
            {value.campaign.scheduleUseAustralianEastern
              ? 'Times are Australian Eastern (AEST/AEDT) — use only when the ad account is set to the wrong timezone.'
              : `Times are in the ad account's timezone${timezoneName ? ` (${timezoneName})` : ''} — Meta runs the campaign there.`}
          </p>
        </div>
      </div>
    </section>
  );
}
